import React, { useContext, useEffect, useRef, useState, useCallback, useMemo } from "react";
import { Accordion } from "react-bootstrap";
import { GoogleMap, useJsApiLoader, OverlayView, InfoWindow } from "@react-google-maps/api";
import {
  getData,
  postDataWithBody,
} from "../../../Services/Services";
import {
  geteventdetails,
  getincidentcatcreate,
  gisCoordinatesData,
  gisStateCoordinates,
  getThemeURL,
  getPastDaysWeatherData,
  findAllChildrenRegionsById
} from "../../../Services/EndPoints";
import { toast } from "react-toastify";
import { ThemeContext } from "../../..";
import OpenLayerMapLegend from "./OpenLayerMapLegend";
import IncidentLifecycleTrack from "./IncidentLifecycleTrack";
import IncidentQuickLinks from "./IncidentQuickLinks";
import {
  useNearbyFacilities,
  NearbyFacilitiesMarkers,
  NearbyFacilitiesPanel,
  WeatherPanel,
  useGoogleWeather,
  FACILITY_TYPES,
  DirectionsRoute
} from "./Nearbyfacilitieslayer";

const GOOGLE_MAPS_API_KEY = "AIzaSyDLzC-FhQmB8JS2H46OW46DNOEboBbrZVg";
const GOOGLE_MAPS_LIBRARIES = ["drawing", "geometry", "places", "visualization"];

const STATE_COORDINATES = {
  62: { lat: 34.1526, lng: 77.5771 }, // Ladakh
  52: { lat: 25.0961, lng: 85.3131 }, // Bihar
};
const getCenterObj = (id) => STATE_COORDINATES[id] || STATE_COORDINATES[62];
const getCenterArr = (id) => [getCenterObj(id).lat, getCenterObj(id).lng];

const MAP_TYPE_IDS = {
  road: "roadmap",
  satellite: "satellite",
  terrain: "terrain",
  hybrid: "hybrid",
};

// ── Avalanche-matching color tokens ──────────────────────────────────────
const C = {
  bg: "#F0F6FB",
  surface: "#FFFFFF",
  navBg: "#1C4E72",
  primary: "#2A6591",
  primaryDark: "#1C4E72",
  text1: "#0F2D42",
  text2: "#3A5F78",
  text3: "#7A9BB0",
  border: "rgba(42,101,145,.10)",
  shadow: "0 2px 4px rgba(15,45,66,.04), 0 8px 28px rgba(42,101,145,.08)",
};

const INCIDENT_TYPES = {
  FLOOD: { color: "#3B82F6", icon: "💧" },
  Flood: { color: "#3B82F6", icon: "💧" },
  COLD_WAVE: { color: "#6B7280", icon: "❄️" },
  "Cold Wave": { color: "#6B7280", icon: "❄️" },
  EARTHQUAKE: { color: "#8B5CF6", icon: "🌍" },
  Earthquake: { color: "#8B5CF6", icon: "🌍" },
  FIRE: { color: "#DC2626", icon: "🔥" },
  Fire: { color: "#DC2626", icon: "🔥" },
  CYCLONE: { color: "#F59E0B", icon: "🌀" },
  Cyclone: { color: "#F59E0B", icon: "🌀" },
  "Heat Wave": { color: "#F97316", icon: "🔥" },
  HEAT_WAVE: { color: "#F97316", icon: "🔥" },
  Drought: { color: "#D97706", icon: "🌵" },
  DROUGHT: { color: "#D97706", icon: "🌵" },
  Landslide: { color: "#92400E", icon: "⛰️" },
  LANDSLIDE: { color: "#92400E", icon: "⛰️" },
  Tsunami: { color: "#0891B2", icon: "🌊" },
  TSUNAMI: { color: "#0891B2", icon: "🌊" },
  Tornado: { color: "#7C3AED", icon: "🌪️" },
  TORNADO: { color: "#7C3AED", icon: "🌪️" },
  AVALANCHE: { color: "#9333EA", icon: "🏔️" },
  Avalanche: { color: "#9333EA", icon: "🏔️" },
  CLOUDBURST: { color: "#60A5FA", icon: "⛈️" },
  "Cloud Burst": { color: "#60A5FA", icon: "⛈️" },
  CloudBurst: { color: "#60A5FA", icon: "⛈️" },
  Cloud_Burst: { color: "#60A5FA", icon: "⛈️" },
  DEFAULT: { color: "#EF4444", icon: "⚠️" },
};

// ─── Map Loading Overlay (refined) ─────────────────────────────────────────
const MapLoadingOverlay = () => (
  <div style={{
    position: "absolute", inset: 0, zIndex: 1000,
    background: "rgba(10,20,50,0.72)", backdropFilter: "blur(4px)",
    display: "flex", flexDirection: "column", alignItems: "center",
    justifyContent: "center", gap: "18px", borderRadius: "inherit", pointerEvents: "all",
  }}>
    <div style={{ position: "relative", width: 80, height: 80 }}>
      {[0, 1, 2].map((i) => (
        <div key={i} style={{
          position: "absolute", inset: 0, borderRadius: "50%",
          border: `2px solid ${C.primary}`, opacity: 0,
          animation: `radarPulse 1.8s ease-out ${i * 0.6}s infinite`,
        }} />
      ))}
      <div style={{
        position: "absolute", inset: "50%", transform: "translate(-50%,-50%)",
        width: 28, height: 28, borderRadius: "50%",
        background: `linear-gradient(135deg, ${C.primary}, ${C.primaryDark})`,
        boxShadow: `0 0 18px ${C.primary}aa`, display: "flex",
        alignItems: "center", justifyContent: "center", fontSize: 14,
      }}>🗺️</div>
    </div>
    <div style={{ textAlign: "center" }}>
      <div style={{ color: "#E2E8F0", fontSize: 14, fontWeight: 700, fontFamily: "'Sora',sans-serif", letterSpacing: "0.02em", marginBottom: 6 }}>Loading Incidents…</div>
      <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} style={{ width: 6, height: 6, borderRadius: "50%", background: C.primary, animation: `dotBounce 1.2s ease-in-out ${i * 0.18}s infinite` }} />
        ))}
      </div>
    </div>
    <style>{`
      @keyframes radarPulse { 0%{transform:scale(0.3);opacity:0.9} 100%{transform:scale(1.8);opacity:0} }
      @keyframes dotBounce  { 0%,80%,100%{transform:scaleY(1);opacity:0.4} 40%{transform:scaleY(1.7);opacity:1} }
    `}</style>
  </div>
);

// ─── Zoom Controls (Avalanche-style) ───────────────────────────────────────
const ZoomControls = ({ mapInstance }) => {
  const handleZoomIn  = () => { if (mapInstance) mapInstance.setZoom((mapInstance.getZoom() || 7) + 1); };
  const handleZoomOut = () => { if (mapInstance) mapInstance.setZoom((mapInstance.getZoom() || 7) - 1); };

  const baseBtn = {
    width: 36, height: 36,
    background: C.surface, border: "none", cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 20, fontWeight: 400, color: C.text1, lineHeight: 1,
    transition: "background 0.15s, color 0.15s", userSelect: "none",
  };

  return (
    <div style={{
      position: "absolute", top: 114, right: 10, zIndex: 1000,
      display: "flex", flexDirection: "column",
      boxShadow: C.shadow, borderRadius: 12, overflow: "hidden",
      border: `1px solid ${C.border}`,
    }}>
      <button style={baseBtn} onClick={handleZoomIn}
        onMouseEnter={e => { e.currentTarget.style.background = C.bg; e.currentTarget.style.color = C.primary; }}
        onMouseLeave={e => { e.currentTarget.style.background = C.surface; e.currentTarget.style.color = C.text1; }}
        title="Zoom in">+</button>
      <div style={{ width: "100%", height: 1, background: C.border }} />
      <button style={baseBtn} onClick={handleZoomOut}
        onMouseEnter={e => { e.currentTarget.style.background = C.bg; e.currentTarget.style.color = C.primary; }}
        onMouseLeave={e => { e.currentTarget.style.background = C.surface; e.currentTarget.style.color = C.text1; }}
        title="Zoom out">−</button>
    </div>
  );
};

// ─── Custom Animated Marker ────────────────────────────────────────────────
const CustomIncidentMarker = ({ marker, onClick, isOpen, onInfoClose, formatDateDisplay, INCIDENT_TYPES, getIncidentColor }) => {
  const config = INCIDENT_TYPES[marker.category] || INCIDENT_TYPES.DEFAULT;
  const RED = "#DC2626";
  return (
    <>
      <OverlayView position={{ lat: marker.position[0], lng: marker.position[1] }} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
        <div onClick={(e) => { e.stopPropagation(); onClick(); }}
          style={{ position: "relative", width: 50, height: 50, cursor: "pointer", transform: "translate(-25px,-25px)" }}>
          <div style={{ position: "absolute", width: 50, height: 50, borderRadius: "50%", background: `${RED}22`, border: `2px solid ${RED}66`, top: "50%", left: "50%", transform: "translate(-50%,-50%)", animation: "pulse-ring 2s infinite" }} />
          <div style={{ position: "absolute", width: 36, height: 36, borderRadius: "50%", background: `${RED}44`, border: `2px solid ${RED}88`, top: "50%", left: "50%", transform: "translate(-50%,-50%)", animation: "pulse-ring 2s infinite 0.3s" }} />
          <div style={{ position: "absolute", width: 24, height: 24, borderRadius: "50%", background: RED, border: "2px solid white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: "white", top: "50%", left: "50%", transform: "translate(-50%,-50%)", boxShadow: "0 2px 8px rgba(0,0,0,0.3)", zIndex: 10 }}>
            {config.icon}
          </div>
          <style>{`@keyframes pulse-ring{0%{transform:translate(-50%,-50%) scale(0.8);opacity:1}100%{transform:translate(-50%,-50%) scale(1.4);opacity:0}}`}</style>
        </div>
      </OverlayView>
      {isOpen && (() => {
        const hz = getIncidentColor(marker.category);
        const Label = ({ children }) => (
          <div style={{ fontSize: "9.5px", fontWeight: 700, letterSpacing: ".07em", textTransform: "uppercase", color: "#9AB0BF", marginBottom: "2px" }}>{children}</div>
        );
        const Value = ({ children }) => (
          <div style={{ fontSize: "12.5px", fontWeight: 600, color: C.text1, lineHeight: 1.4, wordBreak: "break-word" }}>{children}</div>
        );
        return (
          <InfoWindow position={{ lat: marker.position[0], lng: marker.position[1] }} onCloseClick={onInfoClose}
            options={{ pixelOffset: new window.google.maps.Size(0, -30), maxWidth: 320 }}>
            <div style={{ width: "292px", fontFamily: "'Inter','Segoe UI',sans-serif", margin: "-2px" }}>
              {/* Header */}
              <div style={{ display: "flex", alignItems: "center", gap: "10px", paddingBottom: "12px", borderBottom: `1px solid ${C.border}`, marginBottom: "12px" }}>
                <div style={{ width: 36, height: 36, borderRadius: "10px", background: `${hz}14`, border: `1px solid ${hz}33`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "17px", flexShrink: 0 }}>
                  {INCIDENT_TYPES[marker.category]?.icon || "⚠️"}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: "9px", fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "#9AB0BF" }}>Hazard Type</div>
                  <div style={{ fontSize: "15px", fontWeight: 800, color: hz, lineHeight: 1.2, fontFamily: "'Sora','Inter',sans-serif" }}>{marker.category || "Incident"}</div>
                </div>
                <div style={{ fontSize: "10.5px", fontWeight: 800, color: C.primary, background: "rgba(42,101,145,.08)", border: "1px solid rgba(42,101,145,.16)", borderRadius: "999px", padding: "3px 9px", flexShrink: 0 }}>
                  {marker.incident_seq_id || `ID ${marker.id ?? "—"}`}
                </div>
              </div>

              {/* Fields */}
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                <div><Label>Incident Name</Label><Value>{marker.name || "NA"}</Value></div>
                <div><Label>Incident Date and Time</Label><Value>{formatDateDisplay(marker.created_on) || "NA"}</Value></div>
                <div><Label>Incident Location</Label><Value>{marker.address || "Loading…"}</Value></div>
                <div>
                  <Label>Description</Label>
                  <div style={{ fontSize: "11.5px", lineHeight: 1.5, color: C.text2, background: "#F6F9FB", border: `1px solid ${C.border}`, borderRadius: "8px", padding: "8px 10px", maxHeight: "112px", overflowY: "auto", marginTop: "2px" }}>
                    {marker.description || "No description"}
                  </div>
                </div>
              </div>
            </div>
          </InfoWindow>
        );
      })()}
    </>
  );
};

// ─── GeoJSON Layer ─────────────────────────────────────────────────────────
const GeoJSONLayer = ({ map, data, strokeColor, strokeWeight, fillColor, fillOpacity, getPopupContent }) => {
  const dataLayerRef = useRef(null);
  const infoWindowRef = useRef(null);
  useEffect(() => {
    if (!map || !data || !window.google) return;
    const dataLayer = new window.google.maps.Data({ map });
    dataLayerRef.current = dataLayer;
    dataLayer.addGeoJson(data);
    dataLayer.setStyle({ strokeColor, strokeWeight, fillColor, fillOpacity, strokeOpacity: 1 });
    let clickListener = null;
    if (getPopupContent) {
      const iw = new window.google.maps.InfoWindow();
      infoWindowRef.current = iw;
      clickListener = dataLayer.addListener("click", (event) => {
        iw.setContent(getPopupContent(event.feature));
        iw.setPosition(event.latLng);
        iw.open(map);
      });
    }
    return () => {
      if (clickListener) window.google.maps.event.removeListener(clickListener);
      if (infoWindowRef.current) infoWindowRef.current.close();
      dataLayer.setMap(null);
    };
  }, [map, data, strokeColor, strokeWeight, fillColor, fillOpacity]);
  return null;
};

// ─── Weather Mini Card (Avalanche-style) ───────────────────────────────────
const WeatherMiniCard = ({ weatherCenter, weatherLabel, weatherAddress, weatherDateTime, apiKey, theme, isExpanded, onToggle, weatherRegionId, weatherIncidentDate, pastWeatherData, pastWeatherLoading }) => {
  const { currentWeather: cw, weatherLoading, weatherError } =
    useGoogleWeather(weatherCenter, apiKey, !!weatherCenter);

  const condType  = cw?.weatherCondition?.type || "";
  const condLabel = cw?.weatherCondition?.description?.text || "";
  const tempC     = cw?.temperature?.degrees != null ? Math.round(cw.temperature.degrees) : null;
  const iconUri   = cw?.weatherCondition?.iconBaseUri ? `${cw.weatherCondition.iconBaseUri}.png` : null;
  const isDaytime = cw?.isDaytime ?? true;

  const COND_EMOJI = {
    CLEAR: "☀️", MOSTLY_CLEAR: "🌤️", PARTLY_CLOUDY: "⛅", MOSTLY_CLOUDY: "🌥️",
    CLOUDY: "☁️", WINDY: "💨", RAIN: "🌧️", LIGHT_RAIN: "🌦️", HEAVY_RAIN: "🌧️",
    THUNDERSTORM: "⛈️", LIGHT_SNOW: "❄️", SNOW: "❄️", FOGGY: "🌫️",
    TORNADO: "🌪️", HURRICANE: "🌀",
  };
  const condEmoji = COND_EMOJI[condType] || (isDaytime ? "🌤️" : "🌙");
  const isIncident = weatherLabel && weatherLabel !== "Region Overview";

  return (
    <div style={{
      margin: "12px 14px 0 14px",
      borderRadius: isExpanded ? "14px 14px 0 0" : "14px",
      overflow: "hidden",
      border: "1px solid rgba(42,101,145,0.18)",
      boxShadow: "0 2px 8px rgba(15,45,66,0.06), 0 4px 16px rgba(42,101,145,0.08)",
      background: C.surface,
      transition: "border-radius 0.22s ease",
    }}>
      <div
        onClick={onToggle}
        style={{
          cursor: "pointer",
          background: "linear-gradient(135deg, #FFFFFF 0%, #E8F2FA 100%)",
          padding: "12px 14px",
          display: "flex", alignItems: "stretch", gap: 12, userSelect: "none",
          transition: "filter 0.15s ease",
          borderBottom: isExpanded ? `1px solid ${C.border}` : "none",
        }}
        onMouseEnter={e => e.currentTarget.style.filter = "brightness(0.98)"}
        onMouseLeave={e => e.currentTarget.style.filter = "brightness(1)"}
      >
        {/* Temperature side */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 3, flexShrink: 0, minWidth: 56 }}>
          <div style={{ width: 38, height: 38, display: "flex", alignItems: "center", justifyContent: "center" }}>
            {weatherLoading ? (
              <div style={{ width: 22, height: 22, borderRadius: "50%", border: `2.5px solid ${C.primary}`, borderTopColor: "transparent", animation: "wxSpin 0.75s linear infinite" }} />
            ) : iconUri ? (
              <>
                <img src={iconUri} alt={condType} style={{ width: 36, height: 36, objectFit: "contain" }}
                  onError={e => { e.target.style.display = "none"; e.target.nextSibling.style.display = "inline"; }} />
                <span style={{ fontSize: 28, display: "none" }}>{condEmoji}</span>
              </>
            ) : (
              <span style={{ fontSize: 30, lineHeight: 1 }}>{condEmoji}</span>
            )}
          </div>
          <div style={{ fontSize: 18, fontWeight: 800, lineHeight: 1, color: C.text1, letterSpacing: "-0.5px", fontFamily: "'Sora',sans-serif" }}>
            {tempC !== null ? `${tempC}°C` : weatherError ? "—" : "…"}
          </div>
          <div style={{ fontSize: 10, fontWeight: 600, color: C.primary, textAlign: "center", lineHeight: 1.2, maxWidth: 60 }}>
            {weatherLoading ? "…" : weatherError ? "N/A" : condLabel || condType.replace(/_/g, " ") || "—"}
          </div>
        </div>

        <div style={{ width: 1, background: C.border, borderRadius: 2, flexShrink: 0, alignSelf: "stretch" }} />

        {/* Info side */}
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", justifyContent: "center", gap: 5 }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 5 }}>
            <span style={{ fontSize: 11, flexShrink: 0, marginTop: 1 }}>{isIncident ? "⚠️" : "🌐"}</span>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.text1, lineHeight: 1.35, wordBreak: "break-word", fontFamily: "'Sora',sans-serif" }}>
              {isIncident ? weatherLabel : "Region Overview"}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 5 }}>
            <span style={{ fontSize: 11, flexShrink: 0, marginTop: 1 }}>📍</span>
            <div style={{ fontSize: 11, fontWeight: 500, color: C.text2, lineHeight: 1.35, wordBreak: "break-word" }}>
              {weatherAddress || "Fetching address…"}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <span style={{ fontSize: 10, flexShrink: 0 }}>🕐</span>
            <div style={{ fontSize: 10, fontWeight: 500, color: C.text3 }}>{weatherDateTime || "—"}</div>
          </div>
        </div>

        {/* Expand chevron */}
        <div style={{
          alignSelf: "center", flexShrink: 0, width: 24, height: 24, borderRadius: "50%",
          background: "rgba(42,101,145,.08)", border: `1px solid ${C.border}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 10, color: C.primary, fontWeight: 700,
          transition: "transform 0.25s cubic-bezier(.4,0,.2,1)",
          transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
        }}>▼</div>
      </div>

      {isExpanded && (
        <div style={{ borderTop: `1px solid ${C.border}`, animation: "wxSlideDown 0.2s ease-out" }}>
          <WeatherPanel
            incidentCenter={weatherCenter} incidentName={weatherLabel}
            apiKey={apiKey} enabled={true} theme={theme}
            displayDateTime={weatherDateTime} weatherRegionId={weatherRegionId}
            weatherIncidentDate={weatherIncidentDate} pastWeatherData={pastWeatherData}
            pastWeatherLoading={pastWeatherLoading}
          />
        </div>
      )}

      <style>{`
        @keyframes wxSpin      { to { transform: rotate(360deg); } }
        @keyframes wxSlideDown { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
};

// ─── Layer Switcher (Avalanche-style card) ─────────────────────────────────
const LayerSwitcherPanel = ({ isOpen, setIsOpen, activeBaseLayer, setActiveBaseLayer }) => {
  const layers = [
    { id: "road", name: "Road View", icon: "🛣️" },
    { id: "satellite", name: "Satellite", icon: "🛰️" },
    { id: "terrain", name: "Terrain", icon: "⛰️" },
    { id: "hybrid", name: "Hybrid", icon: "🗺️" },
  ];

  return (
    <div style={{ position: "absolute", top: 10, right: 10, zIndex: 1000 }}>
      <button onClick={() => setIsOpen(!isOpen)}
        style={{
          width: 44, height: 44, backgroundColor: C.surface,
          border: `1px solid ${C.border}`, borderRadius: 12, cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: C.shadow, transition: "transform .15s, box-shadow .15s",
        }}
        onMouseOver={e => { e.currentTarget.style.transform = "scale(1.04)"; e.currentTarget.style.boxShadow = "0 4px 20px rgba(15,45,66,.14)"; }}
        onMouseOut={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.boxShadow = C.shadow; }}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke={C.text1} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M2 12L12 17L22 12" stroke={C.text2} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M2 17L12 22L22 17" stroke={C.text3} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {isOpen && (
        <div style={{
          position: "absolute", top: 52, right: 0,
          background: C.surface, borderRadius: 14,
          boxShadow: "0 8px 32px rgba(15,45,66,.16)",
          minWidth: 190, overflow: "hidden",
          border: `1px solid ${C.border}`,
          animation: "wxSlideDown 0.15s ease-out",
        }}>
          <div style={{
            padding: "10px 14px",
            background: "linear-gradient(135deg, #F5FBFF, #EBF4FA)",
            borderBottom: `1px solid ${C.border}`,
            fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 11,
            color: C.text1, textTransform: "uppercase", letterSpacing: ".04em",
          }}>Base Layers</div>
          {layers.map(layer => (
            <div key={layer.id}
              onClick={() => { setActiveBaseLayer(layer.id); setIsOpen(false); }}
              style={{
                padding: "10px 14px", cursor: "pointer",
                background: activeBaseLayer === layer.id ? "rgba(42,101,145,.06)" : C.surface,
                borderBottom: `1px solid rgba(42,101,145,.04)`,
                display: "flex", alignItems: "center", gap: 10,
                transition: "background .12s, transform .12s",
                borderLeft: activeBaseLayer === layer.id ? `3px solid ${C.primary}` : "3px solid transparent",
              }}
              onMouseEnter={e => { if (activeBaseLayer !== layer.id) e.currentTarget.style.background = "rgba(42,101,145,.04)"; }}
              onMouseLeave={e => { if (activeBaseLayer !== layer.id) e.currentTarget.style.background = C.surface; }}>
              <span style={{ fontSize: 14 }}>{layer.icon}</span>
              <span style={{ fontSize: 12, fontWeight: activeBaseLayer === layer.id ? 700 : 500, color: activeBaseLayer === layer.id ? C.primary : C.text1 }}>
                {layer.name}
              </span>
              {activeBaseLayer === layer.id && (
                <span style={{ marginLeft: "auto", color: C.primary, fontSize: 11, fontWeight: 800 }}>✓</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────
const OpenLayerSource = ({ fromDate, toDate }) => {
  const [incidentid, setIncidentid]                = useState("");
  const [incident, setIncident]                    = useState([]);
  const [allIncidents, setAllIncidents]            = useState([]);
  const [selectedIncidentId, setSelectedIncidentId] = useState("");
  const [incidentCenter, setIncidentCenter]        = useState(null);
  const [selectedIncident, setSelectedIncident]    = useState([]);
  const [currentImageIndex, setCurrentImageIndex]  = useState(0);
  const [hasEvents, setHasEvents]                  = useState(false);
  const [expandedIncidentId, setExpandedIncidentId] = useState(null);
  const [accordionKey, setAccordionKey]            = useState("0");
  const [stateGeoJSON, setStateGeoJSON]            = useState(null);
  const [districtGeoJSON, setDistrictGeoJSON]      = useState([]);
  const [incidentMarkers, setIncidentMarkers]      = useState([]);
  const [isLoading, setIsLoading]                  = useState(true);
  const [hoveredIncidentId, setHoveredIncidentId]  = useState(null);
  const [clickedIncidentId, setClickedIncidentId]  = useState(null);
  const [openPopupId, setOpenPopupId]              = useState(null);
  const [mapInstance, setMapInstance]              = useState(null);
  const { theme } = useContext(ThemeContext);

  const [isLayerMenuOpen, setIsLayerMenuOpen]       = useState(false);
  const [activeBaseLayer, setActiveBaseLayer]       = useState("terrain");
  const [showIncidentMarkers, setShowIncidentMarkers]       = useState(true);
  const [showDistrictBoundaries, setShowDistrictBoundaries]  = useState(true);
  const [showStateBoundary, setShowStateBoundary]   = useState(true);
  const [stateId, setStateId] = useState(null);

  const [weatherExpanded, setWeatherExpanded] = useState(false);
  const [weatherCenter, setWeatherCenter] = useState(null);
  const [weatherLabel, setWeatherLabel] = useState("Region Overview");

  const [showNearbyFacilities, setShowNearbyFacilities] = useState(false);
  const [nearbyFacilityOpenPopupId, setNearbyFacilityOpenPopupId] = useState(null);
  const [enabledFacilityTypes, setEnabledFacilityTypes] = useState(new Set(FACILITY_TYPES.map((f) => f.key)));
  const [nearbySearchCenter, setNearbySearchCenter] = useState(null);
  const [nearbyIncidentName, setNearbyIncidentName] = useState("");
  const [selectedFacilityId, setSelectedFacilityId] = useState(null);
  const [routeInfo, setRouteInfo] = useState(null);

  const [weatherRegionId, setWeatherRegionId] = useState(null);
  const [weatherIncidentDate, setWeatherIncidentDate] = useState(null);
  const [pastWeatherData, setPastWeatherData] = useState(null);
  const [pastWeatherLoading, setPastWeatherLoading] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showLifecycle, setShowLifecycle] = useState(true);
  const mapContainerRef = useRef(null);

  const getCurrentDateTime = () => {
    const now = new Date();
    const day   = String(now.getDate()).padStart(2, "0");
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const year  = now.getFullYear();
    let hours   = now.getHours();
    const mins  = String(now.getMinutes()).padStart(2, "0");
    const ampm  = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    return `${day}-${month}-${year} ${String(hours).padStart(2, "0")}:${mins} ${ampm}`;
  };

  const [weatherAddress, setWeatherAddress] = useState("");
  const [weatherDateTime, setWeatherDateTime] = useState(getCurrentDateTime());

  const { facilities, loading: facilitiesLoading } =
    useNearbyFacilities(mapInstance, nearbySearchCenter, showNearbyFacilities && !!nearbySearchCenter);

  const { isLoaded: mapsLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const onMapLoad    = useCallback((map) => setMapInstance(map), []);
  const onMapUnmount = useCallback(() => setMapInstance(null), []);
  const MAP_CENTER = useMemo(() => getCenterObj(stateId), [stateId]);

  const mapOptions = useMemo(() => ({
    mapTypeId: MAP_TYPE_IDS[activeBaseLayer] || "terrain",
    minZoom: 4,
    scrollwheel: true,
    disableDefaultUI: true,
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: false,
    zoomControl: false,
  }), [activeBaseLayer]);

  useEffect(() => {
    if (!stateId) return;
    loadIncidents(); mapStateCoordinates(); loadDistrictBoundaries();
  }, [fromDate, toDate, stateId]);

  useEffect(() => {
    if (mapInstance && incidentCenter) {
      mapInstance.panTo({ lat: incidentCenter[0], lng: incidentCenter[1] });
      mapInstance.setZoom(12);
    }
  }, [incidentCenter, mapInstance]);

  useEffect(() => {
    if (!stateId) return;
    const fetchDefaultAddress = async () => {
      const [lat, lng] = getCenterArr(stateId);
      setWeatherCenter([lat, lng]);
      const addr = await reverseGeocode(lat, lng);
      setWeatherAddress(addr);
      setWeatherDateTime(getCurrentDateTime());
      if (mapInstance && !expandedIncidentId) {
        mapInstance.panTo({ lat, lng });
        mapInstance.setZoom(7);
      }
    };
    fetchDefaultAddress();
  }, [stateId, mapInstance, expandedIncidentId]);

  const getUserProfile = async () => {
    try {
      const profile = await getData(getThemeURL);
      if (profile && profile.length > 0) {
        setStateId(profile[0].state_id);
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
    }
  };

  useEffect(() => {
    if (!stateId) return;
    mapStateCoordinates();
    loadIncidents();
  }, [stateId]);

  const mapStateCoordinates = async () => {
    try {
      const res = await getData(gisStateCoordinates + String(stateId));
      if (res && res.length > 0) {
        const stateData = res[0];
        const geoJsonData = {
          type: "Feature",
          properties: { name: stateData.state_name, state_id: stateData.state_id },
          geometry: JSON.parse(stateData.coordinates),
        };
        setStateGeoJSON(geoJsonData);
      }
    } catch (error) {
      console.error("Error fetching state coordinates:", error);
      toast.error("Failed to load state coordinates");
    }
  };

  const loadDistrictBoundaries = async () => {
    if (!stateId) return;
    try {
      const districtsList = await postDataWithBody(findAllChildrenRegionsById, [stateId], {});
      if (!districtsList || !Array.isArray(districtsList)) return;
      const districtFeatures = [];
      for (const district of districtsList) {
        if (!district.gis_id) continue;
        try {
          const res = await getData(`${gisCoordinatesData}${district.gis_id}`);
          if (res && res.length > 0) {
            districtFeatures.push({
              type: "Feature",
              properties: { name: district.name || res[0].district, dist_code: res[0].dist_code, area_km: res[0].area_km },
              geometry: JSON.parse(res[0].coordinates),
            });
          }
        } catch (e) { /* skip district if coordinates fail */ }
      }
      setDistrictGeoJSON(districtFeatures);
    } catch (e) { console.error(e); toast.error("Failed to load district boundaries"); }
  };

  const reverseGeocode = async (lat, lng) => {
    try {
      const res = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GOOGLE_MAPS_API_KEY}`);
      const data = await res.json();
      if (data.status === "OK" && data.results?.length > 0) {
        const get = (components, type) => components.find(c => c.types.includes(type))?.long_name || "";
        for (const result of data.results) {
          const formatted = result.formatted_address || "";
          const isPlusCode = /^[A-Z0-9]{4,8}\+[A-Z0-9]{2,3}/.test(formatted.trim());
          if (isPlusCode) continue;
          const components = result.address_components || [];
          const parts = [];
          const village  = get(components, "sublocality_level_1") || get(components, "sublocality") || get(components, "neighborhood");
          const locality = get(components, "locality");
          const district = get(components, "administrative_area_level_3") || get(components, "administrative_area_level_2");
          const state    = get(components, "administrative_area_level_1");
          const country  = get(components, "country");
          if (village) parts.push(village); else if (locality) parts.push(locality);
          if (district && district !== locality) parts.push(district);
          if (state) parts.push(state);
          if (country) parts.push(country);
          if (parts.length > 0) return parts.join(", ");
          if (formatted && !isPlusCode) return formatted;
        }
        const fallback = data.results[0].formatted_address || "";
        const cleaned  = fallback.replace(/^[A-Z0-9+]+,?\s*/, "").trim();
        return cleaned || `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
      }
      return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
    } catch { return `${lat.toFixed(4)}, ${lng.toFixed(4)}`; }
  };

  const fetchPastWeather = async (regionId, incidentDate) => {
    if (!regionId || !incidentDate) return;
    try {
      setPastWeatherLoading(true);
      setPastWeatherData(null);
      const dateStr = new Date(incidentDate).toISOString().split("T")[0];
      const url = getPastDaysWeatherData.replace("REGIONID", regionId).replace("DATE", dateStr);
      const data = await getData(url);
      setPastWeatherData(data);
    } catch (e) {
      console.error("Past weather fetch failed:", e);
      setPastWeatherData(null);
    } finally { setPastWeatherLoading(false); }
  };

  const formatDateDisplay = (dateTimeString) => {
    if (!dateTimeString) return "-";
    try {
      if (/^\d{2}-\d{2}-\d{4}\s\d{2}:\d{2}\s[AP]M$/.test(dateTimeString)) return dateTimeString;
      const date = new Date(dateTimeString);
      if (isNaN(date)) return "-";
      const day = String(date.getDate()).padStart(2, "0"), month = String(date.getMonth() + 1).padStart(2, "0"), year = date.getFullYear();
      let hours = date.getHours(); const minutes = String(date.getMinutes()).padStart(2, "0"), ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12 || 12;
      return `${day}-${month}-${year} ${String(hours).padStart(2, "0")}:${minutes} ${ampm}`;
    } catch { return dateTimeString; }
  };

  const loadIncidents = async () => {
    setIsLoading(true);
    try {
      const activeincidents = await getData(getincidentcatcreate.replace("STATEID", stateId));
      if (activeincidents && Array.isArray(activeincidents) && activeincidents.length > 0) {
        const filteredIncidents = (fromDate && toDate)
          ? activeincidents.filter(inc => {
              if (!inc.created_on) return true;
              const incDate = new Date(inc.created_on);
              return incDate >= fromDate && incDate <= toDate;
            })
          : activeincidents;

        setAllIncidents(filteredIncidents);
        const markers = (await Promise.all(filteredIncidents.map(async (inc) => {
          try {
            if (inc.gis_config) {
              const gisConfig = JSON.parse(inc.gis_config);
              if (gisConfig.incidentcenter && Array.isArray(gisConfig.incidentcenter)) {
                const [lng, lat] = gisConfig.incidentcenter;
                const address = await reverseGeocode(lat, lng);
                return { id: inc.id, incident_seq_id: inc.incident_seq_id, position: [lat, lng], name: inc.name, description: inc.description, category: inc.category, created_on: inc.created_on, address };
              }
            }
            return null;
          } catch { return null; }
        }))).filter(Boolean);
        setIncidentMarkers(markers);
      } else if (activeincidents?.data && Array.isArray(activeincidents.data)) {
        const filteredIncidents = (fromDate && toDate)
          ? activeincidents.data.filter(inc => {
              if (!inc.created_on) return true;
              const incDate = new Date(inc.created_on);
              return incDate >= fromDate && incDate <= toDate;
            })
          : activeincidents.data;
        setAllIncidents(filteredIncidents);
      }
    } catch (e) { console.error(e); }
    finally { setIsLoading(false); }
  };

  const handleIncidentClick = async (id) => {
    try {
      setIncidentid(id); setSelectedIncidentId(id); setCurrentImageIndex(0);
      const details = await postDataWithBody(geteventdetails, [id], {});
      setIncident(details);
      const hasEv = details && details.length > 0;
      setHasEvents(hasEv);
      if (hasEv) {
        setSelectedIncident(details);
        const lat = parseFloat(details[0]?.lat), lng = parseFloat(details[0]?.lng);
        if (!isNaN(lat) && !isNaN(lng)) setIncidentCenter([lat, lng]);
        else setIncidentCenter(null);
      } else { setSelectedIncident([]); setIncidentCenter(null); }
    } catch { setHasEvents(false); setSelectedIncident([]); setIncidentCenter(null); }
  };

  const clearNearby = () => {
    setNearbySearchCenter(null);
    setNearbyIncidentName("");
    setShowNearbyFacilities(false);
    setSelectedFacilityId(null);
    setRouteInfo(null);
  };

  const handleShowAll = () => {
    setExpandedIncidentId(null); setSelectedIncident([]); setHasEvents(false);
    setIncidentCenter(null); setClickedIncidentId(null); setOpenPopupId(null);
    clearNearby();
    setWeatherCenter(getCenterArr(stateId));
    setWeatherLabel("Region Overview");
    setSelectedFacilityId(null);
    setWeatherRegionId(null);
    setWeatherIncidentDate(null);
    setPastWeatherData(null);
    if (mapInstance) { mapInstance.panTo(MAP_CENTER); mapInstance.setZoom(7); }
    const [lat, lng] = getCenterArr(stateId);
    reverseGeocode(lat, lng).then(addr => {
      setWeatherAddress(addr);
      setWeatherDateTime(getCurrentDateTime());
    });
  };

  useEffect(() => { getUserProfile(); }, []);

  const toggleIncidentExpand = (id) => {
    setClickedIncidentId(id);
    setTimeout(() => setClickedIncidentId(null), 450);
    if (expandedIncidentId === id) {
      setExpandedIncidentId(null); setSelectedIncident([]); setHasEvents(false);
      setIncidentCenter(null); setOpenPopupId(null); clearNearby();
      setWeatherCenter(getCenterArr(stateId));
      setWeatherLabel("Region Overview");
      return;
    }
    setExpandedIncidentId(id);
    setShowLifecycle(true);
    setOpenPopupId(null); // popup opens only on a direct marker click, not on list select
    handleIncidentClick(id);
    const m = incidentMarkers.find((m) => m.id === id);
    if (m && mapInstance) {
      mapInstance.panTo({ lat: m.position[0], lng: m.position[1] });
      mapInstance.setZoom(8);
    }
    const incData = allIncidents.find(i => i.id === id);
    if (incData) {
      setWeatherRegionId(incData.region_ids || null);
      setWeatherIncidentDate(incData.created_on || null);
      fetchPastWeather(incData.region_ids, incData.created_on);
    }
    if (m) {
      setWeatherCenter(m.position);
      setWeatherLabel(m.name || "Selected Incident");
      setWeatherAddress(m.address || "Fetching address…");
      setWeatherDateTime(getCurrentDateTime());
    }
  };

  const handleToggleNearbyFacilities = (inc) => {
    const marker = incidentMarkers.find((m) => m.id === inc.id);
    if (!marker) { toast.warning("Incident location not available"); return; }
    const alreadyActive = showNearbyFacilities && nearbySearchCenter &&
      nearbySearchCenter[0] === marker.position[0] && nearbySearchCenter[1] === marker.position[1];
    if (alreadyActive) {
      setShowNearbyFacilities(false); setNearbySearchCenter(null); setNearbyIncidentName("");
    } else {
      setShowNearbyFacilities(true); setNearbySearchCenter(marker.position); setNearbyIncidentName(inc.name);
      if (mapInstance) { mapInstance.panTo({ lat: marker.position[0], lng: marker.position[1] }); mapInstance.setZoom(11); }
    }
  };

  const handleToggleFacilityType = (key) => {
    setEnabledFacilityTypes((prev) => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  };

  const getIncidentColor = (cat) => INCIDENT_TYPES[cat]?.color || "#EF4444";

  const visibleMarkers = expandedIncidentId ? incidentMarkers.filter((m) => m.id === expandedIncidentId) : incidentMarkers;

  // ── Fullscreen support ──────────────────────────────────────────────────
  useEffect(() => {
    const handleFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      mapContainerRef.current?.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  }, []);

  return (
    <div className="d-flex" style={{ height: "100%", width: "100%" }}>
      {/* ── Left: Map ── */}
      <div ref={mapContainerRef} style={{ flex: 1, position: "relative", zIndex: 1, height: isFullscreen ? "100vh" : "100%" }}>
        {isLoading && <MapLoadingOverlay />}
        <LayerSwitcherPanel
          isOpen={isLayerMenuOpen}
          setIsOpen={setIsLayerMenuOpen}
          activeBaseLayer={activeBaseLayer}
          setActiveBaseLayer={setActiveBaseLayer}
        />

        {/* Fullscreen toggle button */}
        <button
          onClick={toggleFullscreen}
          title={isFullscreen ? "Exit Full Screen" : "Full Screen"}
          style={{
            position: "absolute", top: 62, right: 10, zIndex: 1000,
            width: 44, height: 44, borderRadius: 12,
            border: isFullscreen ? "2px solid #3b82f6" : `1px solid ${C.border}`,
            background: isFullscreen ? "linear-gradient(135deg,#eff6ff,#dbeafe)" : C.surface,
            cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: isFullscreen ? "0 2px 12px rgba(59,130,246,0.35)" : C.shadow,
            transition: "transform .15s, box-shadow .15s",
          }}
          onMouseOver={e => { e.currentTarget.style.transform = "scale(1.04)"; e.currentTarget.style.boxShadow = "0 4px 20px rgba(15,45,66,.14)"; }}
          onMouseOut={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.boxShadow = isFullscreen ? "0 2px 12px rgba(59,130,246,0.35)" : C.shadow; }}
        >
          {isFullscreen ? (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3" />
            </svg>
          ) : (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={C.text1} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
            </svg>
          )}
        </button>

        <ZoomControls mapInstance={mapInstance} />

        {mapsLoaded && stateId ? (
          <GoogleMap
            mapContainerStyle={{ height: "100%", width: "100%" }}
            center={MAP_CENTER}
            zoom={7}
            options={mapOptions}
            onLoad={onMapLoad}
            onUnmount={onMapUnmount}
          >
            {stateGeoJSON && showStateBoundary && mapInstance && (
              <GeoJSONLayer map={mapInstance} data={stateGeoJSON} strokeColor="#b11119" strokeWeight={2} fillColor="transparent" fillOpacity={0.2}
                getPopupContent={(f) => `<div style="font-family:'Inter',sans-serif;padding:4px"><strong>State:</strong> ${f.getProperty("name")}</div>`} />
            )}
            {showDistrictBoundaries && mapInstance && districtGeoJSON.map((d, i) => (
              <GeoJSONLayer key={`district-${i}`} map={mapInstance} data={d} strokeColor="#FF8C00" strokeWeight={2} fillColor="#FFA500" fillOpacity={0.2}
                getPopupContent={(f) => `<div style="font-family:'Inter',sans-serif;padding:4px"><strong>District:</strong> ${f.getProperty("name")}<br/><strong>Code:</strong> ${f.getProperty("dist_code")}<br/><strong>Area:</strong> ${f.getProperty("area_km")} km²</div>`} />
            ))}
            {showIncidentMarkers && visibleMarkers.map((marker) => (
              <CustomIncidentMarker key={marker.id} marker={marker}
                isOpen={openPopupId === marker.id}
                onClick={() => setOpenPopupId(openPopupId === marker.id ? null : marker.id)}
                onInfoClose={() => setOpenPopupId(null)}
                formatDateDisplay={formatDateDisplay}
                INCIDENT_TYPES={INCIDENT_TYPES}
                getIncidentColor={getIncidentColor} />
            ))}
            {showNearbyFacilities && (
              <NearbyFacilitiesMarkers
                facilities={facilities}
                enabledTypes={enabledFacilityTypes}
                openPopupId={nearbyFacilityOpenPopupId}
                onOpen={(id) => setNearbyFacilityOpenPopupId(id)}
                onClose={() => setNearbyFacilityOpenPopupId(null)}
                mapInstance={mapInstance}
                nearbySearchCenter={nearbySearchCenter}
                selectedFacilityId={selectedFacilityId}
              />
            )}
            {showNearbyFacilities && selectedFacilityId && nearbySearchCenter && (() => {
              const allPlaces = Object.values(facilities).flat();
              const selectedPlace = allPlaces.find(p => p.place_id === selectedFacilityId);
              const facConfig = selectedPlace
                ? FACILITY_TYPES.find(f => facilities[f.key]?.some(p => p.place_id === selectedFacilityId))
                : null;
              return (
                <DirectionsRoute
                  key={selectedFacilityId}
                  mapInstance={mapInstance}
                  origin={nearbySearchCenter}
                  destinationPlaceId={selectedFacilityId}
                  facilityConfig={facConfig}
                  onRouteFetched={setRouteInfo}
                />
              );
            })()}
          </GoogleMap>
        ) : (
          <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Inter',sans-serif", color: C.text3, fontSize: 13 }}>
            Loading Map Data…
          </div>
        )}

        <div style={{ position: "absolute", top: 16, left: 12, zIndex: 500, pointerEvents: "auto" }}>
          <OpenLayerMapLegend
            incidentMarkers={incidentMarkers} expandedIncidentId={expandedIncidentId}
            allIncidents={allIncidents} showIncidentMarkers={showIncidentMarkers}
            showDistrictBoundaries={showDistrictBoundaries} showStateBoundary={showStateBoundary}
            mapInstance={mapInstance} isPopupOpen={openPopupId !== null} />
        </div>

        {/* ── Incident Lifecycle track — shown at the bottom of the map when
            an incident is selected (mirrors the View Incident stepper) ── */}
        {expandedIncidentId && showLifecycle && (
          <IncidentLifecycleTrack
            incident={allIncidents.find((i) => i.id === expandedIncidentId)}
            incidentName={
              allIncidents.find((i) => i.id === expandedIncidentId)?.name
            }
            onClose={() => setShowLifecycle(false)}
          />
        )}
      </div>

      {/* ── Right Panel ── */}
      <div className="rid-right-panel" style={{
        width: "380px",
        height: "100%",
        background: C.bg,
        color: C.text1,
        overflowY: "auto",
        overflowX: "hidden",
        borderLeft: `1px solid ${C.border}`,
        fontFamily: "'Inter','Segoe UI',sans-serif",
      }}>

        {/* Weather Card — expands fully, no scroll constraint */}
        <WeatherMiniCard
          weatherCenter={weatherCenter} weatherLabel={weatherLabel}
          weatherAddress={weatherAddress} weatherDateTime={weatherDateTime}
          apiKey={GOOGLE_MAPS_API_KEY} theme={theme}
          isExpanded={weatherExpanded} onToggle={() => setWeatherExpanded(prev => !prev)}
          weatherRegionId={weatherRegionId} weatherIncidentDate={weatherIncidentDate}
          pastWeatherData={pastWeatherData} pastWeatherLoading={pastWeatherLoading}
        />

        {weatherExpanded && (
          <div style={{
            margin: "0 14px", height: 8,
            background: C.bg,
            borderLeft: `1.5px solid ${C.border}`,
            borderRight: `1.5px solid ${C.border}`,
            borderBottom: `1.5px solid ${C.border}`,
            borderRadius: "0 0 12px 12px",
          }} />
        )}

        {/* Incidents List — same padding as weather card margins for alignment */}
        <div style={{ padding: "14px" }}>
          {/* Incident count badge — Avalanche-style */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            marginBottom: 14, padding: "0 2px",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontFamily: "'Sora',sans-serif", fontSize: ".82rem", fontWeight: 700, color: C.primaryDark }}>
                All Incidents
              </span>
              <span style={{
                background: C.primary, color: "#fff",
                fontSize: ".6rem", fontWeight: 800, padding: "2px 10px",
                borderRadius: 20, letterSpacing: ".03em",
              }}>{allIncidents.length}</span>
            </div>
            {expandedIncidentId && (
              <button onClick={handleShowAll}
                style={{
                  padding: "4px 12px", background: "#EEF6FB", color: C.primary,
                  border: "1px solid rgba(42,101,145,0.16)", borderRadius: 20,
                  fontSize: ".67rem", fontWeight: 700, cursor: "pointer",
                  fontFamily: "'Inter',sans-serif", transition: "background .13s",
                  display: "inline-flex", alignItems: "center", gap: 5,
                  whiteSpace: "nowrap",
                }}
                onMouseOver={(e) => e.currentTarget.style.background = "#DBEDF7"}
                onMouseOut={(e) => e.currentTarget.style.background = "#EEF6FB"}>
                ← Show All
              </button>
            )}
          </div>

          {allIncidents.length > 0 ? (
            (expandedIncidentId ? allIncidents.filter((inc) => inc.id === expandedIncidentId) : allIncidents).map((inc, idx) => {
              const isSelected  = inc.id === expandedIncidentId;
              const isHovered   = inc.id === hoveredIncidentId && !isSelected;
              const isClicked   = inc.id === clickedIncidentId;
              const accent      = getIncidentColor(inc.category);
              const config      = INCIDENT_TYPES[inc.category] || INCIDENT_TYPES.DEFAULT;
              const marker      = incidentMarkers.find((m) => m.id === inc.id);
              const isFacActive = showNearbyFacilities && nearbySearchCenter &&
                marker?.position[0] === nearbySearchCenter[0] && marker?.position[1] === nearbySearchCenter[1];

              return (
                <div key={inc.id} style={{ marginBottom: 10, animation: `rid-fade-in 0.3s ease ${idx * 0.04}s both` }}>
                  <div
                    onMouseEnter={() => setHoveredIncidentId(inc.id)}
                    onMouseLeave={() => setHoveredIncidentId(null)}
                    onClick={() => toggleIncidentExpand(inc.id)}
                    style={{
                      cursor: "pointer",
                      background: C.surface,
                      borderRadius: 14,
                      overflow: "hidden",
                      border: isSelected
                        ? `1px solid rgba(42,101,145,.18)`
                        : `1px solid ${C.border}`,
                      borderLeft: isSelected
                        ? `4px solid ${C.primary}`
                        : isHovered
                          ? `4px solid rgba(42,101,145,.35)`
                          : "4px solid transparent",
                      transition: "all 0.22s cubic-bezier(.16,1,.3,1)",
                      boxShadow: isSelected
                        ? `0 4px 20px rgba(42,101,145,.14), 0 0 0 1px rgba(42,101,145,.08)`
                        : isHovered
                          ? "0 4px 16px rgba(42,101,145,.10)"
                          : "0 1px 3px rgba(15,45,66,.04), 0 4px 14px rgba(42,101,145,.06)",
                      transform: isSelected ? "translateX(2px)" : isHovered ? "translateX(1px)" : "none",
                    }}>

                    {/* Card header */}
                    <div style={{
                      position: "relative", overflow: "hidden",
                      padding: "14px 16px 12px",
                      background: isSelected
                        ? "linear-gradient(135deg, #EEF6FB 0%, #E4F0F8 100%)"
                        : C.surface,
                    }}>
                      {isClicked && <span className="incident-card-ripple" style={{ background: `${C.primary}30`, top: "30%", left: "40%" }} />}

                      {/* Row 1: hazard type pill + date */}
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 6 }}>
                        <div style={{
                          display: "inline-flex", alignItems: "center", gap: 5,
                          background: `${accent}0F`, border: `1px solid ${accent}22`,
                          borderRadius: 20, padding: "3px 10px", flexShrink: 0,
                        }}>
                          <span style={{ fontSize: 12 }}>{config.icon}</span>
                          <span style={{ fontSize: ".62rem", fontWeight: 700, color: accent, letterSpacing: ".02em" }}>
                            {inc.cat?.trim() || inc.category?.trim() || "Incident"}
                          </span>
                        </div>
                        <span style={{ fontSize: ".58rem", fontWeight: 500, color: C.text3, whiteSpace: "nowrap", flexShrink: 0 }}>
                          {formatDateDisplay(inc.created_on)}
                        </span>
                      </div>

                      {/* Row 2: incident sequence id */}
                      <div style={{ marginBottom: 10 }}>
                        <span style={{
                          display: "inline-block", fontSize: ".58rem", fontWeight: 700, color: C.primary,
                          background: "rgba(42,101,145,.08)", border: "1px solid rgba(42,101,145,.14)",
                          borderRadius: 20, padding: "2px 9px", letterSpacing: ".02em",
                        }}>
                          {inc.incident_seq_id || `ID: ${inc.id ?? "NA"}`}
                        </span>
                      </div>

                      {/* Incident name */}
                      <div style={{
                        fontFamily: "'Sora',sans-serif",
                        fontSize: ".82rem", fontWeight: 700,
                        color: C.text1, lineHeight: 1.35,
                        marginBottom: 8, letterSpacing: "-.01em",
                      }}>
                        {inc.name?.trim() || "Unnamed Incident"}
                      </div>

                      {/* Description */}
                      {inc.description && (
                        <div style={{
                          fontSize: ".72rem", fontWeight: 500,
                          color: C.text2, lineHeight: 1.55,
                          display: "-webkit-box", WebkitLineClamp: isSelected ? 10 : 2,
                          WebkitBoxOrient: "vertical", overflow: "hidden",
                        }}>
                          {inc.description.trim()}
                        </div>
                      )}
                    </div>

                    {/* Linked Modules — same navigation as View Incident's Quick Links */}
                    {isSelected && <IncidentQuickLinks incident={inc} />}

                    {/* Card footer actions — Avalanche badge button style */}
                    <div style={{
                      padding: "9px 16px", display: "flex", alignItems: "center", gap: 8,
                      borderTop: "1px solid rgba(42,101,145,.07)",
                      background: "linear-gradient(135deg, #F5FBFF 0%, #EBF4FA 100%)",
                    }} onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => handleToggleNearbyFacilities(inc)}
                        style={{
                          padding: "5px 12px",
                          background: isFacActive ? "#ECFDF5" : "#EEF6FB",
                          color: isFacActive ? "#059669" : C.primary,
                          border: `1px solid ${isFacActive ? "#10B981" : "rgba(42,101,145,.16)"}`,
                          borderRadius: 20, fontSize: ".62rem", fontWeight: 700, cursor: "pointer",
                          display: "flex", alignItems: "center", gap: 5,
                          transition: "all 0.18s ease",
                          fontFamily: "'Inter',sans-serif",
                          boxShadow: isFacActive ? "0 2px 8px rgba(16,185,129,.2)" : "none",
                        }}
                        onMouseEnter={e => { if (!isFacActive) e.currentTarget.style.background = "#DBEDF7"; }}
                        onMouseLeave={e => { if (!isFacActive) e.currentTarget.style.background = "#EEF6FB"; }}>
                        <span style={{ fontSize: 11 }}>{isFacActive ? "✅" : "📍"}</span>
                        {isFacActive ? "Hide Facilities" : "Nearby Facilities"}
                      </button>
                      {/* {marker && (
                        <span style={{ fontSize: ".56rem", color: C.text3, fontWeight: 500, marginLeft: "auto", maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          📌 {marker.address?.split(",")[0] || "—"}
                        </span>
                      )} */}
                    </div>

                    {isFacActive && (
                      <div onClick={(e) => e.stopPropagation()} style={{ borderTop: `1px solid rgba(42,101,145,.06)` }}>
                        <NearbyFacilitiesPanel
                          facilities={facilities} loading={facilitiesLoading}
                          enabledTypes={enabledFacilityTypes} onToggleType={handleToggleFacilityType}
                          incidentName={inc.name} theme={theme} mapInstance={mapInstance}
                          nearbySearchCenter={nearbySearchCenter} selectedFacilityId={selectedFacilityId}
                          onSelectFacility={(id) => { setSelectedFacilityId(id); if (!id) setRouteInfo(null); }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <div style={{
              textAlign: "center", padding: "48px 24px",
              background: C.surface, borderRadius: 14,
              border: `2px dashed ${C.border}`,
            }}>
              <div style={{
                width: 64, height: 64, borderRadius: 18,
                background: "linear-gradient(145deg, #EBF4FA, #DCEdf5)",
                display: "flex", alignItems: "center", justifyContent: "center",
                margin: "0 auto 14px", fontSize: 28,
              }}>🔍</div>
              <div style={{ fontSize: ".82rem", fontWeight: 700, color: C.text2, fontFamily: "'Sora',sans-serif" }}>No incidents found</div>
              <div style={{ fontSize: ".7rem", fontWeight: 500, color: C.text3, marginTop: 6, lineHeight: 1.5 }}>Try adjusting the date range to<br/>discover reported incidents</div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes rid-fade-in {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default OpenLayerSource;